import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Bardo (manuale V1, rr. 2338-2415).
 *
 * - **3 mosse automatiche** (`type: 'starting'`): *Parole di Potere*,
 *   *Un Porto nella Tempesta*, *Piano d'Azione*.
 * - **4 mosse di scelta** (`type: 'starting'`, `pickCount: 2` nel gruppo
 *   `startingChoiceGroups`): *Conoscenze Bardiche*, *Scavezzacollo*,
 *   *Pathos*, *Affascinante e Onesto*. Il giocatore ne sceglie **due** alla
 *   creazione (manuale r. 2360: "scegli **due** delle seguenti mosse").
 *   Prima vera applicazione di `pickCount: 2` su `StartingMoveChoiceGroup`.
 *
 * **Caso peculiare** *Conoscenze Bardiche*: il manuale (rr. 2362-2382) dà 8
 * aree di competenza tra cui scegliere. Allo stato attuale lo schema
 * `MoveSchema.choices` permette di rappresentarle ma il manuale non specifica
 * un `choiceCount` — sembra essere "scegli 1, prendi tutta la mossa, e con
 * la mossa avanzata *Vaste Conoscenze* aggiungi 2 aree". Modello come
 * `choices` con `choiceCount: 1`. *Vaste Conoscenze* (L2-5) estende la lista.
 */

const paroleDiPotere: Move = {
  id: 'bardo.parole-di-potere',
  name: 'Parole di Potere',
  type: 'starting',
  classId: 'bardo',
  trigger: 'Quando sprigioni il vero potere della Parola',
  description:
    "Scegli un alleato che può sentirti (escluso te stesso) e un effetto:\n\n- lenisci il suo dolore e lo guarisci di 1d8 PF;\n- la prossima volta che infligge danni con un attacco, infligge +1d4 danni;\n- si riprende da stordimento, sonno o lieve confusione mentale;\n- il primo che prova ad ***aiutarlo*** (ma non a *ostacolarlo*!) ottiene +1 al tiro;\n\nDopodiché tira+**CAR**. Con un 10+, ottieni l'effetto desiderato. Con un 7-9, la magia funziona, ma attiri attenzioni indesiderate, oppure il potere della Parola riverbera anche su altri bersagli (a scelta del GM).",
  roll: {
    stat: 'carisma',
    on10plus: { text: "Ottieni l'effetto desiderato sull'alleato scelto." },
    on7to9: {
      text: 'La magia funziona, ma attiri attenzioni indesiderate o riverbera su altri bersagli (GM sceglie).',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unPortoNellaTempesta: Move = {
  id: 'bardo.un-porto-nella-tempesta',
  name: 'Un Porto nella Tempesta',
  type: 'starting',
  classId: 'bardo',
  trigger:
    'Quando torni in un luogo civilizzato che hai visitato in precedenza (a tua discrezione)',
  description:
    "Dì al GM quando ci sei stato l'ultima volta, e lui ti dirà che cosa è cambiato da allora.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const pianoDAzione: Move = {
  id: 'bardo.piano-d-azione',
  name: "Piano d'Azione",
  type: 'starting',
  classId: 'bardo',
  trigger: "Quando ti occorrono una via d'uscita o un diversivo",
  description:
    "Descrivi cosa ti servirebbe. Se la cosa è anche solo minimamente plausibile, ci sarà sempre nei dintorni un candelabro, una fune, una finestra, un carro, una mandria di bestiame da spaventare o un'altra opportunità simile, pronta per essere sfruttata.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const conoscenzeBardiche: Move = {
  id: 'bardo.conoscenze-bardiche',
  name: 'Conoscenze Bardiche',
  type: 'starting',
  classId: 'bardo',
  trigger:
    'Quando ti imbatti per la prima volta in una creatura, un luogo o un oggetto importante (a tua discrezione) che rientri nelle tue aree di competenza',
  description:
    "Scegli un'area di competenza. Quando il trigger si verifica, puoi fare al GM una domanda al riguardo e ti verrà data una risposta sincera. Il GM potrà chiederti da quale racconto, canzone o leggenda hai tratto tale informazione.",
  tags: [],
  prerequisites: [],
  replaces: [],
  choices: [
    { id: 'incantesimi-e-magie', label: 'Incantesimi e Magie' },
    { id: 'morti-e-non-morti', label: 'Morti e Non Morti' },
    { id: 'grandi-storie-mondo-conosciuto', label: 'Grandi Storie del Mondo Conosciuto' },
    { id: 'arte-architettura', label: 'Arte e Architettura del Passato e del Presente' },
    { id: 'bestiario-creature-inconsuete', label: 'un Bestiario di Creature Inconsuete' },
    { id: 'sfere-planari', label: 'le Sfere Planari' },
    { id: 'leggende-antichi-eroi', label: 'Leggende di Antichi Eroi' },
    { id: 'dei-e-loro-servitori', label: 'gli Dei e i Loro Servitori' },
  ],
  choiceCount: 1,
};

const scavezzacollo: Move = {
  id: 'bardo.scavezzacollo',
  name: 'Scavezzacollo',
  type: 'starting',
  classId: 'bardo',
  trigger: 'Quando ti lanci in una prodezza folle e acrobatica, ridendo baldanzoso',
  description:
    "Descrivila e tira+**DES**. Con un 10+, scegli 3. Con un 7-9, scegli 2:\n\n- finisci esattamente dove volevi;\n- non attiri attenzioni indesiderate;\n- fornisci un'opportunità a un alleato. Descrivi quale e a chi;\n- chi vede la peripezia ne rimane impressionato, e, se ne esce vivo, alimenterà la tua leggenda;\n- crei scompiglio: sarà difficile seguirti, ma anche gli alleati ne saranno intralciati (il GM dirà come).",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Scegli 3 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 2 opzioni dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const pathos: Move = {
  id: 'bardo.pathos',
  name: 'Pathos',
  type: 'starting',
  classId: 'bardo',
  trigger: 'Quando fai un 6- usando una mossa',
  description:
    'Se hai meno di 2 prese, ne prendi 1. Spendi una presa per ottenere un 10+ in *aiutare o ostacolare*, senza dover tirare i dadi. Descrivi come il Fato ci mette lo zampino.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const affascinanteEOnesto: Move = {
  id: 'bardo.affascinante-e-onesto',
  name: 'Affascinante e Onesto',
  type: 'starting',
  classId: 'bardo',
  trigger: 'Quando parli onestamente con qualcuno',
  description:
    'Puoi fare al giocatore che lo interpreta (il GM se è un PNG) una domanda tra le seguenti:\n\n- di chi sei al servizio?\n- cosa vorresti che io facessi?\n- come posso fare in modo che tu _____?\n- cosa provi realmente in questo momento?\n- qual è il tuo desiderio più grande?\n\nEgli dovrà rispondere onestamente e potrà farti a sua volta una domanda della lista (a cui tu dovrai dare una risposta sincera).',
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 */
export const bardoStartingAutoMoves: readonly Move[] = [
  paroleDiPotere,
  unPortoNellaTempesta,
  pianoDAzione,
] as const;

/**
 * Mosse iniziali di SCELTA (4): il giocatore ne sceglie DUE al livello 1
 * (`pickCount: 2`).
 */
export const bardoStartingChoiceMoves: readonly Move[] = [
  conoscenzeBardiche,
  scavezzacollo,
  pathos,
  affascinanteEOnesto,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const bardoInitialMoves: readonly Move[] = [
  ...bardoStartingAutoMoves,
  ...bardoStartingChoiceMoves,
];
